/**
 * 选择器状态 URL 化同步层 — 挂在 Layout 上。
 *
 * 职责：让 company/brand/product 三级选中态与 URL query 双向同步，
 * 读取层（21 个页面从 useCompanyStore 读选中态）零改动。
 *
 * 同步规则（写死）：
 * 1. **冲突时 URL 优先** —— URL 是选择态的唯一权威来源。
 *    URL 带 company/brand/product 任一参数 → 以 URL 为准回写 store。
 * 2. URL 无参 → 用 store 现值补齐 URL（replace 模式，不污染浏览器历史）。
 * 3. store 变化（选择器 onChange / 品牌产品自动选中）→ 同步写 URL（replace）。
 *
 * 实现要点：拆两个 effect 防止互相打架 ——
 * - Effect A 只响应 URL 变化（规则 1/2）；
 * - Effect B 只响应 store 选中 ID 变化（规则 3），带相等守卫，写完即稳态。
 *
 * 详情页（/products/:id 等）以 path 参数优先、store 兜底，与本层互不干扰。
 */
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useCompanyStore } from "../store/companyStore";

/** 受管理的 query 键。 */
const PARAM_KEYS = ["company", "brand", "product"] as const;

export function useSyncSelectionToUrl() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);
  const selectCompany = useCompanyStore((s) => s.selectCompany);
  const selectBrand = useCompanyStore((s) => s.selectBrand);
  const selectProduct = useCompanyStore((s) => s.selectProduct);

  const search = searchParams.toString();

  // Effect A —— URL → store（规则 1：URL 优先）+ URL 无参时回填（规则 2）。
  useEffect(() => {
    const params = new URLSearchParams(search);
    const urlHasSelection = PARAM_KEYS.some((k) => params.has(k));

    if (urlHasSelection) {
      // 规则 1：URL 优先。以 URL 为准回写 store（缺失键按空串处理）。
      const urlCompany = params.get("company") || "";
      const urlBrand = params.get("brand") || "";
      const urlProduct = params.get("product") || "";
      // 级联顺序写回：company → brand → product
      //（selectCompany 会重置品牌/产品，selectBrand 会重置产品，必须按序执行）
      if (urlCompany !== useCompanyStore.getState().selectedCompanyId) {
        selectCompany(urlCompany);
      }
      if (urlBrand !== useCompanyStore.getState().selectedBrandId) {
        selectBrand(urlBrand);
      }
      if (urlProduct !== useCompanyStore.getState().selectedProductId) {
        selectProduct(urlProduct);
      }
      return;
    }

    // 规则 2：URL 无参 → 用 store 现值补齐 URL（replace）。
    const s = useCompanyStore.getState();
    if (s.selectedCompanyId || s.selectedBrandId || s.selectedProductId) {
      const next = new URLSearchParams(search);
      if (s.selectedCompanyId) next.set("company", s.selectedCompanyId);
      if (s.selectedBrandId) next.set("brand", s.selectedBrandId);
      if (s.selectedProductId) next.set("product", s.selectedProductId);
      if (next.toString() !== search) {
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Effect B —— store → URL（规则 3：onChange / 自动选中后同步写 URL）。
  useEffect(() => {
    const params = new URLSearchParams(search);
    const urlCompany = params.get("company") || "";
    const urlBrand = params.get("brand") || "";
    const urlProduct = params.get("product") || "";
    // 相等守卫：store 与 URL 已一致则不动，避免无意义导航。
    if (
      urlCompany === selectedCompanyId &&
      urlBrand === selectedBrandId &&
      urlProduct === selectedProductId
    ) {
      return;
    }
    const next = new URLSearchParams(search);
    if (selectedCompanyId) next.set("company", selectedCompanyId);
    else next.delete("company");
    if (selectedBrandId) next.set("brand", selectedBrandId);
    else next.delete("brand");
    if (selectedProductId) next.set("product", selectedProductId);
    else next.delete("product");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, selectedBrandId, selectedProductId]);
}
